import React, { useState, useEffect } from 'react';
import { Form, Button, Container } from 'react-bootstrap';
import { apiPost } from '../../api/apiClient';
import ChangePasswordForm from './ChangePasswordForm';
import logo from '../login/logo.png';
import RTSpinner from '../common/RTSpinner';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import './LoginForm.css';
import tenantStorage from '../../utils/tenantStorage';

const modeMessages = {
    SESSION_INACTIVE: "You have been signed out due to inactivity. Please sign in again.",
    SESSION_EXPIRED: "Your session has expired. Please sign in again.",
    SESSION_LOGGED_OUT: "You have been logged out. Please sign in again.",
    PASSWORD_CHANGED: "Your password has been changed successfully. Please sign in again with your new password.",
    AUTH_NOT_CONFIGURED: "Authentication is not configured for this tenant. Please contact the administrator."
};

const LoginForm = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showWarning, setShowWarning] = useState(true);
    const [mode, setMode] = useState('SESSION_LOGIN');
    const [session, setSession] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    
    // Set mode from navigation state, default to SESSION_LOGIN
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlMode = params.get('mode');
        const stateMode = location.state?.mode;
        
        if (urlMode) {
            setMode(urlMode);
        } else {
            setMode(stateMode || 'SESSION_LOGIN');
        }
        
        // Clear form when mode changes (except for initial SESSION_LOGIN)
        if (stateMode && stateMode !== 'SESSION_LOGIN') {
            setUsername('');
            setPassword('');
            setError('');
            setSession(null);
            setShowWarning(true);
        }
        
        // If coming from NEW_PASSWORD_REQUIRED flow, ensure we reset properly
        if (mode === 'NEW_PASSWORD_REQUIRED' && stateMode) {
            setMode(stateMode);
        }
    }, [location.state, location.search]);

    // Redirect to dashboard if already logged in
    useEffect(() => {
        const accessToken = tenantStorage.getItem('accessToken');
        if (accessToken) {
            navigate('/dashboard');
        }
    }, [navigate]);

    const handleSubmit = async (e) => {
        setShowWarning(false);
        e.preventDefault();
        if (!username || !password) {
            setError('Username and password cannot be empty');
            return;
        }
        setLoading(true);
        setError('');
        login(username, password).then((response) => {
            if (response.challenge_name === 'NEW_PASSWORD_REQUIRED') {
                setMode('NEW_PASSWORD_REQUIRED');
                setSession(response.session);
            } else {
                tenantStorage.setItem('accessToken', response.access_token);
                tenantStorage.setItem('refreshToken', response.refresh_token);
                tenantStorage.setItem('userRole', response.user_role);
                navigate('/dashboard');
            }
        }).catch((err) => {
            if (err.message && err.message.indexOf('401') !== -1) {
                setError('Username or password is incorrect');
            } else {
                setError('An error occurred while logging in. Please try again.');
            }
        }).finally(() => {
            setLoading(false);
        });
    };

    // Function to handle login
    const login = async (uname, pwd) => {
        const response = await apiPost('/login', { username: uname, password: pwd });
        return response.data;
    };

    // Function to refresh access token
    const refreshAccessToken = async () => {
        const refreshToken = tenantStorage.getItem('refreshToken');
        const response = await apiPost('/refresh-token', { refresh_token: refreshToken });
        // Save new tokens
        tenantStorage.setItem('accessToken', response.data.access_token);
        if (response.data.refresh_token) {
            tenantStorage.setItem('refreshToken', response.data.refresh_token);
        }
        return response.data.access_token;
    };

    if (mode === 'NEW_PASSWORD_REQUIRED') {
        return <ChangePasswordForm mode={mode} session={session} username={username} />;
    }

    return (
        <div className="login-container">
            {!loading && (
                <>
                    <div>
                        <img 
                            src={logo}
                            alt="App Logo" 
                            className="login-logo"
                        />
                    </div>
                    <Form onSubmit={handleSubmit} className="login-form">
                        {/* Info message for session modes */}
                        {modeMessages[mode] && showWarning && (
                            <div className={`alert text-center ${mode === 'PASSWORD_CHANGED' ? 'alert-success' : 'alert-info'}`}>
                                {modeMessages[mode]}
                            </div>
                        )}
                        {error && <div className="alert alert-danger">{error}</div>}
                        <Form.Group controlId="formBasicEmail" className="form-group">
                            <Form.Label>Username/Email:</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username or email"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group controlId="formBasicPassword" className="form-group">
                            <Form.Label>Password:</Form.Label>
                            <div className="password-input-container">
                                <Form.Control
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="password-input"
                                />
                                <RTButton
                                    type="button"
                                    variant="link"
                                    className="password-toggle-btn"
                                    iconMode={true}
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10.584 10.587C10.218 10.958 10 11.459 10 12C10 13.105 10.895 14 12 14C12.541 14 13.042 13.782 13.413 13.416" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M9.363 5.365C10.212 5.125 11.106 5 12 5C19 5 23 12 23 12C22.393 13.433 21.374 14.703 20.061 15.692" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M6.646 6.646C4.264 8.167 1.789 10.644 1 12C1 12 5 19 12 19C13.394 19 14.716 18.736 15.934 18.264" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </RTButton>
                            </div>
                        </Form.Group>
                        <RTButton type="submit" className="w-100">
                            Login
                        </RTButton>
                    </Form>
                    
                    {/* Terms and Privacy links at bottom center */}
                    <div className="terms-link-container">
                        <a href="/terms" className="terms-link">
                            Terms & Conditions
                        </a>
                        <span className="link-separator">•</span>
                        <a href="/privacy" className="terms-link">
                            Privacy Policy
                        </a>
                    </div>
                </>
            )}
            {loading && (
                <RTSpinner message="Signing you in..." fullScreen={true} />
            )}
        </div>
    );
};

export default LoginForm;
